import { useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CaretDown, CaretUp, FileXls, MagnifyingGlass, NotePencil, PencilSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Badge, Card, act, cleanLabel, fmtDate, useLoad } from "./shared";
import SubmissionDialog from "./SubmissionDialog";
import ManualEntryPanel from "./ManualEntryPanel";

const sel = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700";
const COLS = [["work_date", "Work date"], ["employee", "Employee"], ["location_count", "Loc."], ["job", "Jobsites"], ["submitted_at", "Submitted at"], [null, "Source"], [null, "Status"], [null, ""]];
const SORT_KEY = "ts_raw_sort";
const SRC = { strata: ["blue", "Strata form"], manual: ["amber", "Manual"], sheet: ["slate", "Typeform"] };

function SortTh({ col, label, sort, onSort }) {
  if (!col) return <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">{label}</th>;
  const on = sort.col === col;
  return (
    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
      <button type="button" onClick={() => onSort(col)} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
        className={`inline-flex items-center gap-1 uppercase tracking-wider ${on ? "text-[#1f64c2]" : "hover:text-slate-800"}`} data-testid={`raw-sort-${col}`}>
        {label}
        <span className="inline-flex flex-col leading-none -space-y-1">
          <CaretUp size={9} weight="bold" className={on && sort.dir === "asc" ? "opacity-100" : "opacity-30"} />
          <CaretDown size={9} weight="bold" className={on && sort.dir === "desc" ? "opacity-100" : "opacity-30"} />
        </span>
      </button>
    </th>
  );
}

function FeedSwitch({ mode, onChange }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-500" data-testid="raw-feed-mode">
      Data store
      <select className={sel} value={mode} onChange={(e) => onChange(e.target.value)} data-testid="raw-feed-select">
        <option value="strata">Strata store (normal)</option>
        <option value="sheet">Raw Google Sheet (emergency rollback)</option>
      </select>
    </label>
  );
}

const loadSort = () => { try { return JSON.parse(sessionStorage.getItem(SORT_KEY)) || { col: "work_date", dir: "desc" }; } catch { return { col: "work_date", dir: "desc" }; } };

export default function SubmissionsTab({ access }) {
  const [f, setF] = useState({ start_date: "", end_date: "", employee: "", job: "", source: "", status: "", q: "" });
  const [limit, setLimit] = useState(100);
  const [open, setOpen] = useState(null);
  const [mode, setMode] = useState(access.feed_mode);
  const [syncing, setSyncing] = useState(false);
  const [manual, setManual] = useState(false);
  const [sort, setSort] = useState(loadSort);
  const onSort = (col) => setSort((s) => { const n = { col, dir: s.col === col && s.dir === "desc" ? "asc" : "desc" }; sessionStorage.setItem(SORT_KEY, JSON.stringify(n)); return n; });
  const params = Object.fromEntries(Object.entries({ ...f, limit_: limit, sort: sort.col, dir: sort.dir }).filter(([, v]) => v !== ""));
  const { data, loading, reload } = useLoad("/timesheet/raw/submissions", params);
  const up = (k) => (e) => { setLimit(100); setF((x) => ({ ...x, [k]: e.target.value })); };

  const exportXlsx = async () => {
    const r = await act(() => api.get("/timesheet/raw/export", { params, responseType: "blob" }));
    if (!r) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r);
    a.download = `strata-timesheet-raw-${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
  };
  const sync = async () => {
    setSyncing(true);
    const r = await act(() => api.post("/timesheet/raw/sync"));
    setSyncing(false);
    if (r) { toast(`Synced · ${r.sheet_rows} Typeform/Sheet rows, ${r.strata_rows} Strata form rows`); reload(); }
  };
  const changeMode = async (m) => {
    if (m === "sheet" && !window.confirm("Emergency rollback: read the raw Google Sheet only? Strata form, manual entries and corrections stop counting until you switch back.")) return;
    if (await act(() => api.put("/timesheet/raw/feed", { mode: m }), m === "sheet" ? "Dashboard now reads the raw Google Sheet" : "Dashboard now reads the Strata store")) setMode(m);
  };

  const items = data?.items || [];
  return (
    <Card
      title="Submissions" testId="raw-submissions"
      subtitle={data ? `${data.total.toLocaleString()} record${data.total === 1 ? "" : "s"} · Typeform/Google Sheet rows sync automatically alongside Strata form and manual entries` : "Loading…"}
      right={
        <div className="flex flex-wrap items-center gap-2">
          {access.is_super_admin && <FeedSwitch mode={mode} onChange={changeMode} />}
          {access.timesheet_raw_edit && <Button size="sm" onClick={() => setManual(true)} disabled={manual} className="bg-[#f59e0b] hover:bg-[#d97706] text-white" data-testid="raw-manual-entry-btn"><NotePencil size={14} className="mr-1.5" />Manual Entry</Button>}
          <Button variant="outline" size="sm" onClick={sync} disabled={syncing} data-testid="raw-sync-btn"><ArrowsClockwise size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />Sync Sheet</Button>
          <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="raw-export-btn"><FileXls size={14} className="mr-1.5 text-emerald-600" />Export Excel</Button>
        </div>
      }
    >
      {manual && <ManualEntryPanel onClose={() => setManual(false)} onSaved={reload} />}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
        <Input type="date" className="cal-white h-9" value={f.start_date} onChange={up("start_date")} data-testid="raw-filter-start" aria-label="From" />
        <Input type="date" className="cal-white h-9" value={f.end_date} onChange={up("end_date")} data-testid="raw-filter-end" aria-label="To" />
        <select className={sel} value={f.employee} onChange={up("employee")} data-testid="raw-filter-employee">
          <option value="">All employees</option>{(data?.employees || []).map((n) => <option key={n}>{n}</option>)}
        </select>
        <select className={sel} value={f.job} onChange={up("job")} data-testid="raw-filter-job">
          <option value="">All jobs</option>{(data?.jobs || []).map((n) => <option key={n} value={n}>{cleanLabel(n)}</option>)}
        </select>
        <select className={sel} value={f.source} onChange={up("source")} data-testid="raw-filter-source">
          <option value="">All sources</option><option value="strata">Strata form</option><option value="manual">Manual</option><option value="sheet">Typeform / Sheet</option>
        </select>
        <select className={sel} value={f.status} onChange={up("status")} data-testid="raw-filter-status">
          <option value="">Active + voided</option><option value="active">Active</option><option value="voided">Voided</option><option value="removed_from_sheet">Removed from Sheet</option>
        </select>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="h-9 pl-8" placeholder="Search" value={f.q} onChange={up("q")} data-testid="raw-filter-search" />
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm" data-testid="raw-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{COLS.map(([c, h]) => <SortTh key={h || "edit"} col={c} label={h} sort={sort} onSort={onSort} />)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((r) => (
              <tr key={r.id} className={`hover:bg-slate-50/70 cursor-pointer ${r.status !== "active" ? "opacity-60" : ""}`} onClick={() => setOpen(r.id)} data-testid={`raw-row-${r.id}`}>
                <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">{fmtDate(r.work_date)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.employee}</td>
                <td className="px-3 py-2 tabular-nums">{r.location_count}</td>
                <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(r.job_labels || []).filter(Boolean).map((j, i) => <Badge key={i} tone={j === "VACATION" ? "amber" : "slate"}>{cleanLabel(j)}</Badge>)}</div></td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500 tabular-nums">{r.submitted_at_display || "—"}</td>
                <td className="px-3 py-2"><Badge tone={(SRC[r.source] || SRC.sheet)[0]} testId={`raw-source-${r.id}`}>{(SRC[r.source] || SRC.sheet)[1]}</Badge></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.status === "active" ? <Badge tone="green">Active</Badge> : r.status === "voided" ? <Badge tone="red">Voided</Badge> : <Badge tone="amber">Removed from Sheet</Badge>}
                  {r.edited && <span className="ml-1"><Badge tone="amber">Corrected</Badge></span>}
                  {r.review_flag && <span className="ml-1"><Badge tone="red">Review: possible duplicate</Badge></span>}
                </td>
                <td className="px-3 py-2 text-right"><PencilSimple size={14} className="text-slate-400 inline" /></td>
              </tr>
            ))}
            {!loading && !items.length && <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400" data-testid="raw-empty">No submissions match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && items.length < data.total && (
        <div className="mt-3 text-center"><Button variant="outline" size="sm" onClick={() => setLimit((l) => Math.min(l + 100, 500))} disabled={limit >= 500} data-testid="raw-load-more">{limit >= 500 ? "Narrow the filters to see more" : "Load more"}</Button></div>
      )}
      {open && <SubmissionDialog id={open} canEdit={access.timesheet_raw_edit} onClose={() => setOpen(null)} onSaved={reload} />}
    </Card>
  );
}
